/**
 * Hook: PUT /api/t/{slug}/candidates/{id} — full PATCH-like update.
 *
 * Distinct from `useUpdateCandidateStatus` (30b — status-only narrow
 * mutation) — this hook covers the full update surface (name,
 * linkedin_url, text, resume_html, status). The narrow status hook stays
 * because it's wired into the Dashboard's status dropdown and we don't
 * want a fat invalidation cascade for what is otherwise a column flip.
 *
 * A30e-1 + A30e-3 wiring (Batch 30e):
 *   When `linkedin_url` is in the input, we bump the per-candidate photo
 *   version counter in TanStack cache. `<CandidatePhoto version={n}>`
 *   reads it and appends `?v={n}` to the photo URL so the browser
 *   bypasses its HTTP cache. The server-side cache is also invalidated
 *   (A30e-1, ``services/candidates.py``); both layers need to flip.
 *
 * On success: invalidates the whole `['candidates', tenantSlug]` prefix
 * + the single-candidate row + bumps the photo version when applicable.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";
import type { Candidate } from "./useCandidates";
import type { CandidateStatus } from "@/components/candidates/StatusPill";

export interface CandidateUpdateInput {
  id: string;
  name?: string;
  /** Pass `null` to unset; omit to leave unchanged. */
  linkedin_url?: string | null;
  /**
   * B-07.3 inline edit — contact fields. Pass `""` to clear (the backend
   * trims and treats an empty string as null); omit to leave unchanged.
   */
  email?: string | null;
  phone?: string | null;
  text?: string | null;
  resume_html?: string | null;
  status?: CandidateStatus;
}

export function useUpdateCandidate(): UseMutationResult<
  Candidate,
  ApiError,
  CandidateUpdateInput
> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const qc = useQueryClient();

  return useMutation<Candidate, ApiError, CandidateUpdateInput>({
    mutationFn: async ({ id, ...rest }) => {
      const r = await apiClient.put<Candidate>(
        `/api/t/${tenantSlug}/candidates/${id}`,
        rest,
      );
      return r.data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ["candidates", tenantSlug] });
      void qc.invalidateQueries({
        queryKey: ["candidate", tenantSlug, variables.id],
      });
      // A30e-3: bump the photo version when linkedin_url is in the input.
      // `linkedin_url: null` (unset) also bumps — the cached photo is no
      // longer correct either way.
      if (Object.prototype.hasOwnProperty.call(variables, "linkedin_url")) {
        qc.setQueryData<number>(
          ["candidate-photo-version", tenantSlug, variables.id],
          (prev) => (prev ?? 0) + 1,
        );
      }
    },
  });
}

// NOTE: a `useCandidatePhotoVersion(candidateId)` reader was drafted for
// consumers like `<CandidatePage>` / `<CandidatesTable>` to read the
// per-candidate cache-busting counter and pass it to `<CandidatePhoto
// version={...}>`. Removed from 30e to keep ts-prune clean — those pages
// live in 30c / 30d lanes. Wire it back up via:
//   const v = qc.getQueryData<number>(['candidate-photo-version', slug, id]) ?? 0;
// in the consumer, then pass to `<CandidatePhoto version={v}>`. BACKLOG
// item P3-30e-photo-version-consumer tracks the wire-up.
